import { useEffect, useState } from "react";
import { axiosInstance } from "@/utilities";
import { API_URL_STORES } from "@/services/XCONS_ENV";

export interface OperatorStore {
  code: string;
  name: string;
  /** Lo pide `SelectedStoresContext` como `seller_id` — es el id del store, no de un seller. */
  storeId: number;
}

/**
 * Los stores donde este operador puede **operar** — que no son los mismos que administra.
 *
 * Antes cada pantalla lo resolvía sola, leyendo `permissions.store.store_permission` del token. Eso
 * tiene dos problemas:
 *
 * 1. **Deja afuera al operador de seller.** No pertenece a ningún store: su acceso viene de los
 *    stores a los que su seller está linkeado y activo. Con el bucket del token, el selector le
 *    salía vacío y la pantalla decía "No tenés stores asignados" — que era falso.
 * 2. **El label era el código capitalizado** (`comercialcmp` → `Comercialcmp`), porque el token no
 *    tiene los nombres. El endpoint sí.
 *
 * `includeSellerLinked=true` es lo que pide la lista ampliada. Es un opt-in y no el default: la
 * sección Stores de Administración pide la misma URL sin el flag, porque vender a través de un
 * store no es administrarlo.
 */
export function useOperatorStores(): { stores: OperatorStore[]; loading: boolean } {
  const [stores, setStores] = useState<OperatorStore[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let vigente = true;
    axiosInstance
      .get(`${API_URL_STORES}?includeSellerLinked=true&limit=200`)
      .then((r) => {
        if (!vigente) return;
        const filas = (r.data?.data ?? []) as {
          code?: string;
          name?: string;
          store_id?: number;
        }[];
        setStores(
          filas
            .filter((f): f is { code: string; name?: string; store_id?: number } => !!f.code)
            .map((f) => ({ code: f.code, name: f.name || f.code, storeId: f.store_id ?? 0 })),
        );
      })
      .catch(() => {
        // Un fallo de carga no es una falta de permiso: se deja la lista vacía y `loading` en
        // false, y quien llama decide qué mostrar. No se cachea el error.
        if (vigente) setStores([]);
      })
      .finally(() => {
        if (vigente) setLoading(false);
      });
    return () => {
      vigente = false;
    };
  }, []);

  return { stores, loading };
}
